import { useLayoutEffect, useRef, useState } from 'react'

// Shared chart hover tooltip — the XD "data hover box" design used by every
// line/area/bar/combo chart across the dashboards and the Data Analytics
// Visualize/Validate tabs, so the hover experience reads identically everywhere.
//
// Layout (per XD):
//   • white rounded card with a soft shadow
//   • a muted date/time header
//   • one block per series: a hollow ring in the series colour + the series
//     label, then the reading in large bold below it
//
// Recharts passes { active, payload, label }. The host chart injects:
//   • formatLabel(label) -> the header string (date/time)
//   • describe(entry)     -> { name, valueText, color } | null   (null = skip)
// keeping each chart's metadata/precision/unit logic where it already lives.
export function ChartHoverTooltip({ active, payload, label, formatLabel, describe }) {
  // ── Keep the box on screen ───────────────────────────────────────────────
  // Recharts clamps the tooltip to the chart's viewBox using the width it
  // measured on the PREVIOUS render. This box is `width: max-content` with
  // nowrap labels, so when the hovered point changes the content can become
  // wider than the width Recharts clamped for — and near the right-hand edge
  // the extra width spills past the card and gets cut off.
  //
  // So we correct it here, after layout: measure the box and nudge it back
  // inside the viewport. The measurement adds the CURRENT shift back out
  // first, so we always reason about the natural (unshifted) position —
  // otherwise shifting would make the next measurement look in-bounds and the
  // box would oscillate. Hooks run before the early return below so the hook
  // order stays stable whether or not the tooltip is showing.
  const boxRef = useRef(null)
  const [shift, setShift] = useState({ x: 0, y: 0 })

  useLayoutEffect(() => {
    const el = boxRef.current
    if (!el) return
    const MARGIN = 8
    const r = el.getBoundingClientRect()
    // Undo the applied shift to get where the box would naturally sit.
    const left   = r.left   - shift.x
    const right  = r.right  - shift.x
    const top    = r.top    - shift.y
    const bottom = r.bottom - shift.y

    let dx = 0
    if (right > window.innerWidth - MARGIN) dx = (window.innerWidth - MARGIN) - right
    if (left + dx < MARGIN) dx = MARGIN - left            // never push it off the left

    let dy = 0
    if (bottom > window.innerHeight - MARGIN) dy = (window.innerHeight - MARGIN) - bottom
    if (top + dy < MARGIN) dy = MARGIN - top

    if (dx !== shift.x || dy !== shift.y) setShift({ x: dx, y: dy })
  })

  if (!active || !Array.isArray(payload) || payload.length === 0) return null

  const items = payload
    .map((entry) => {
      try { return describe ? describe(entry) : null } catch { return null }
    })
    .filter((it) => it && it.name != null && it.valueText != null)

  if (items.length === 0) return null

  const header = formatLabel ? formatLabel(label) : (label != null ? String(label) : '')

  return (
    <div
      ref={boxRef}
      style={{
        // Correction applied by the layout effect above.
        transform: (shift.x || shift.y) ? `translate(${shift.x}px, ${shift.y}px)` : undefined,
        // Compact sizing to match the XD "data hover box": smaller radius,
        // lighter shadow, tighter padding, and a modest value size so the box
        // stays close to the hovered point instead of dominating the chart.
        // Compact XD "data hover box": small radius, light shadow, tight
        // padding. The box sizes to its content (no maxWidth) and each series
        // label stays on ONE line (whiteSpace: nowrap) so the full
        // "device | sensor | parameter (unit)" string is always shown in full —
        // never wrapped or clipped. The host chart passes
        // allowEscapeViewBox so this can't be cut off at the plot's edges.
        position: 'relative',
        background: '#fff',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        border: '1px solid #f0f0f0',
        padding: '11px 16px',
        width: 'max-content',
        maxWidth: 'min(520px, calc(100vw - 32px))',
      }}
    >
      {/* The speech-bubble tail was removed: it sat at the box's centre and did
          not line up with the hovered data point, so it was misleading. */}
      {header && (
        <div style={{ fontSize: 13, color: '#8a8a8a', marginBottom: items.length ? 8 : 0 }}>
          {header}
        </div>
      )}
      {items.map((it, i) => (
        <div key={i} style={{ marginTop: i === 0 ? 0 : 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <span
              style={{
                width: 11,
                height: 11,
                borderRadius: '50%',
                border: `2px solid ${it.color || '#9ca3af'}`,
                background: '#fff',
                flexShrink: 0,
                boxSizing: 'border-box',
              }}
            />
            <span style={{ fontSize: 12, color: '#6b6b6b', lineHeight: 1.3, whiteSpace: 'nowrap' }}>
              {it.name}:
            </span>
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#2b2b2b', marginTop: 2, marginLeft: 18 }}>
            {it.valueText}
          </div>
        </div>
      ))}
    </div>
  )
}

export default ChartHoverTooltip
